/**
 * Login overlay UI: create overlay DOM, show/hide, set error message.
 * Caller wires Google/GitHub button clicks to sign-in.
 */

const OVERLAY_HTML =
  '<div class="rf-login-overlay-inner">' +
  '<h2 class="rf-login-overlay-title">Sign in</h2>' +
  '<p class="rf-login-overlay-desc">Sign in to save your settings, chat, ratings, and presenter access across devices.</p>' +
  '<div class="rf-chat-login-error rf-hidden" role="alert"></div>' +
  '<div class="rf-login-buttons">' +
  '<button type="button" class="rf-chat-login-google"><svg class="rf-login-icon-svg" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.03 24.03 0 0 0 0 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg> Sign in with Google</button>' +
  '<button type="button" class="rf-chat-login-github"><svg class="rf-login-icon-svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg> Sign in with GitHub</button>' +
  '</div>' +
  '<p class="rf-login-overlay-legal">By signing in, you agree that Lectura stores your profile, settings, chat activity, ratings, and presentation interactions to provide the service. Account removal deletes your profile data and anonymizes old chat messages. Privacy requests: <a class="rf-settings-inline-link" href="mailto:hello@lectura.be">hello@lectura.be</a>.</p>' +
  '<button type="button" class="rf-login-overlay-close" aria-label="Close">×</button>' +
  '</div>';

export interface LoginOverlayCallbacks {
  onGoogleClick: () => void;
  onGithubClick: () => void;
}

export interface LoginOverlayApi {
  show: () => void;
  hide: () => void;
  setError: (msg: string) => void;
}

export function createLoginOverlay(callbacks: LoginOverlayCallbacks): LoginOverlayApi {
  let overlayEl: HTMLElement | null = null;

  function setError(msg: string): void {
    if (overlayEl) {
      const err = overlayEl.querySelector('.rf-chat-login-error');
      if (err) {
        err.textContent = msg || '';
        err.classList.toggle('rf-hidden', !msg);
      }
    }
  }

  function hide(): void {
    if (overlayEl) overlayEl.classList.remove('rf-open');
  }

  function show(): void {
    if (overlayEl) {
      overlayEl.classList.add('rf-open');
      return;
    }
    const overlay = document.createElement('div');
    overlay.className = 'rf-login-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Sign in');
    overlay.innerHTML = OVERLAY_HTML;
    const closeBtn = overlay.querySelector('.rf-login-overlay-close');
    const googleBtn = overlay.querySelector('.rf-chat-login-google');
    const githubBtn = overlay.querySelector('.rf-chat-login-github');
    closeBtn?.addEventListener('click', hide);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) hide();
    });
    googleBtn?.addEventListener('click', callbacks.onGoogleClick);
    githubBtn?.addEventListener('click', callbacks.onGithubClick);
    document.body.appendChild(overlay);
    overlay.classList.add('rf-open');
    overlayEl = overlay;
  }

  return { show, hide, setError };
}
